import { format } from "date-fns";

export const isoDay = (d) => format(d, "yyyy-MM-dd");

/** Belegung aus booked_ranges [{check_in, check_out}]: Anreisetag = nachmittags belegt, Abreisetag = vormittags belegt, dazwischen voll. */
export const buildAvailability = (ranges = []) => {
  const full = new Set(), arrival = new Set(), departure = new Set();
  ranges.forEach(({ check_in, check_out }) => {
    if (!check_in || !check_out) return;
    arrival.add(check_in);
    departure.add(check_out);
    const d = new Date(check_in + "T00:00:00"); d.setDate(d.getDate() + 1);
    const end = new Date(check_out + "T00:00:00");
    let guard = 0;
    while (d < end && guard++ < 1000) { full.add(isoDay(d)); d.setDate(d.getDate() + 1); }
  });
  return { full, arrival, departure, ranges: ranges.filter((r) => r.check_in && r.check_out) };
};

/** free | arrival (anderer Gast reist an → nur als Abreise wählbar) | departure (anderer Gast reist ab → nur als Anreise wählbar) | turnover | full */
export const dayState = (av, iso) => {
  if (!av) return "free";
  if (av.full.has(iso)) return "full";
  const a = av.arrival.has(iso), d = av.departure.has(iso);
  return a && d ? "turnover" : a ? "arrival" : d ? "departure" : "free";
};

export const blockedMatcher = (av) => (date) => ["full", "turnover"].includes(dayState(av, isoDay(date)));

export const halfDayModifiers = (av) => ({
  booked: (d) => ["full", "turnover"].includes(dayState(av, isoDay(d))),
  halfArrival: (d) => dayState(av, isoDay(d)) === "arrival",
  halfDeparture: (d) => dayState(av, isoDay(d)) === "departure",
});

/** Kurze Lücken (gap_offer.gaps [{start, end}]): Tage start..end inkl. beider Wechseltage – die freie Hälfte wird grün. */
export const gapMatcher = (gaps) => (d) => { const iso = isoDay(d); return gaps.some((g) => iso >= g.start && iso <= g.end); };

// Diagonal geteilte Tage: unten rechts belegt (fremde Anreise) bzw. oben links belegt (fremde Abreise); Markierung verschwindet bei Auswahl
export const halfDayClassNames = {
  booked: "bg-red-100 text-stone-400 line-through decoration-stone-400",
  halfArrival: "bg-[linear-gradient(to_bottom_right,transparent_50%,#fecaca_50%)] aria-selected:bg-none",
  halfDeparture: "bg-[linear-gradient(to_bottom_right,#fecaca_50%,transparent_50%)] aria-selected:bg-none",
  gap: "[&:not([aria-selected])]:bg-green-100",
};

/** Überschneidung mit einer Belegung? Gleicher Tag An-/Abreise ist erlaubt (Wechseltag). */
export const rangeConflict = (av, from, to) => {
  if (!av || !from || !to) return false;
  const a = isoDay(from), b = isoDay(to);
  return av.ranges.some((r) => a < r.check_out && b > r.check_in);
};

export const RANGE_MESSAGES = {
  de: { arrivalOnly: "An diesem Tag reist ein anderer Gast an – er ist nur als Abreisetag wählbar.", conflict: "Der Zeitraum enthält belegte Nächte – bitte anderen Zeitraum wählen." },
  en: { arrivalOnly: "Another guest arrives on this day – it can only be chosen as your departure date.", conflict: "The selected period includes booked nights – please choose other dates." },
  es: { arrivalOnly: "Ese día llega otro huésped: solo puede elegirse como día de salida.", conflict: "El periodo incluye noches ocupadas; elige otras fechas." },
  nl: { arrivalOnly: "Op deze dag komt een andere gast aan – alleen als vertrekdag te kiezen.", conflict: "De periode bevat bezette nachten – kies andere data." },
  pl: { arrivalOnly: "Tego dnia przyjeżdża inny gość – można go wybrać tylko jako dzień wyjazdu.", conflict: "Wybrany okres zawiera zajęte noce – wybierz inne daty." },
  fr: { arrivalOnly: "Un autre hôte arrive ce jour-là – il ne peut être choisi que comme jour de départ.", conflict: "La période comprend des nuits déjà réservées – veuillez choisir d'autres dates." },
  it: { arrivalOnly: "In questo giorno arriva un altro ospite – può essere scelto solo come giorno di partenza.", conflict: "Il periodo include notti già occupate – scegliere altre date." },
};

/** Auswahl prüfen → { ok, range, message } – bei Konflikt wird die Auswahl auf einen gültigen Anreisetag zurückgesetzt. */
export const validateRange = (av, r, lang = "de") => {
  const msg = RANGE_MESSAGES[lang] || RANGE_MESSAGES.de;
  if (!av || !r?.from) return { ok: true, range: r };
  const fromState = dayState(av, isoDay(r.from));
  if (!r.to) return fromState === "arrival" ? { ok: false, range: { from: undefined, to: undefined }, message: msg.arrivalOnly } : { ok: true, range: r };
  if (rangeConflict(av, r.from, r.to)) {
    const toState = dayState(av, isoDay(r.to));
    return { ok: false, range: { from: toState === "arrival" ? undefined : r.to, to: undefined }, message: msg.conflict };
  }
  return { ok: true, range: r };
};
